import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Chip,
  Tooltip,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Autocomplete,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification, createSuccessNotification } from './common/NotificationSystem';
import {
  NamingConventionConfiguration,
  NamingConventionFormat,
  BlockType,
  AllowedType,
  SuffixType,
  NamingConventionBlock,
  NamingConventionGeneralBlock,
  NamingConventionShortNamesBlock,
  NamingConventionStructureItem,
} from '../types/tagops';

interface NamingConventionRuleFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mode: 'create' | 'edit';
  ruleName?: string;
}

const FORMAT_OPTIONS: { value: NamingConventionFormat; label: string }[] = [
  { value: 'pascal_case', label: 'Pascal Case (ExampleName)' },
  { value: 'camel_case', label: 'Camel Case (exampleName)' },
  { value: 'snake_case', label: 'Snake Case (example_name)' },
  { value: 'kebab_case', label: 'Kebab Case (example-name)' },
  { value: 'screaming_snake_case', label: 'Screaming Snake Case (EXAMPLE_NAME)' },
];

const SUFFIX_OPTIONS: { value: SuffixType; label: string }[] = [
  { value: 'no_suffix', label: 'No Suffix' },
  { value: 'unlimited_suffix', label: 'Unlimited Suffix' },
  { value: 'limited_suffix', label: 'Limited Suffix' },
];

// Helper function to generate naming convention preview
// Returns { main: string, suffix: string } for separate styling
// When suffix is enabled, the last block is part of the suffix
const generateNamingConventionPreview = (
  format: NamingConventionFormat,
  blockNames: string[],
  suffix: SuffixType,
  limitedSuffixRepeatCount?: number
): { main: string; suffix: string } => {
  if (blockNames.length === 0) {
    return { main: 'Add blocks to see preview', suffix: '' };
  }

  // Get the separator based on format
  const getSeparator = (format: NamingConventionFormat): string => {
    switch (format) {
      case 'snake_case':
      case 'screaming_snake_case':
        return '_';
      case 'kebab_case':
        return '-';
      case 'pascal_case':
      case 'camel_case':
        return ''; // No separator for these formats
      default:
        return '-';
    }
  };

  const separator = getSeparator(format);
  
  // When suffix is enabled, the last block is part of the suffix
  const hasSuffix = suffix !== 'no_suffix';
  const mainBlockNames = hasSuffix && blockNames.length > 1 ? blockNames.slice(0, -1) : (hasSuffix ? [] : blockNames);
  const lastBlockName = hasSuffix && blockNames.length > 0 ? blockNames[blockNames.length - 1] : null;
  
  // Create template-style preview using actual block names
  const blockTemplates = mainBlockNames.map((blockName) => `<${blockName}>`);
  const mainPart = blockTemplates.join(separator);
  let suffixPart = '';

  // Add suffix preview (last block + additional suffix blocks) - all suffix blocks are optional
  if (suffix === 'unlimited_suffix') {
    // For unlimited suffix, last block + additional blocks that can repeat (all optional)
    const suffixSeparator = format === 'kebab_case' ? '-' : format === 'snake_case' || format === 'screaming_snake_case' ? '_' : '';
    const prefix = mainPart ? suffixSeparator : '';
    // Last block is optional (wrapped in brackets)
    const lastBlockTemplate = lastBlockName ? `[<${lastBlockName}>]` : '';
    const lastBlockIndex = blockNames.length;
    // Show last block + additional blocks with "..." to indicate it can repeat
    suffixPart = prefix + lastBlockTemplate + suffixSeparator + `[<block${lastBlockIndex + 1}>]${suffixSeparator}[<block${lastBlockIndex + 2}>]${suffixSeparator}...`;
  } else if (suffix === 'limited_suffix') {
    const maxCount = limitedSuffixRepeatCount ?? 1;
    // For limited suffix, all suffix blocks are optional (0 to maxCount allowed)
    const suffixSeparator = format === 'kebab_case' ? '-' : format === 'snake_case' || format === 'screaming_snake_case' ? '_' : '';
    const prefix = mainPart ? suffixSeparator : '';
    // Last block is optional (wrapped in brackets)
    const lastBlockTemplate = lastBlockName ? `[<${lastBlockName}>]` : '';
    const lastBlockIndex = blockNames.length;
    // Additional blocks are also optional (up to maxCount - 1 more)
    const additionalCount = Math.max(0, maxCount - 1);
    const suffixBlocks: string[] = [];
    for (let i = 0; i < additionalCount; i++) {
      suffixBlocks.push(`[<block${lastBlockIndex + 1 + i}>]`);
    }
    suffixPart = prefix + lastBlockTemplate + (suffixBlocks.length > 0 ? suffixSeparator + suffixBlocks.join(suffixSeparator) : '');
  }

  return { main: mainPart, suffix: suffixPart };
};

// Helper function to generate example EC2 instance name
const generateEC2Example = (
  format: NamingConventionFormat,
  blockNames: string[],
  blockList: Record<string, NamingConventionBlock>,
  suffix: SuffixType,
  limitedSuffixRepeatCount?: number
): string => {
  if (blockNames.length === 0) {
    return '';
  }

  const getSeparator = (format: NamingConventionFormat): string => {
    switch (format) {
      case 'snake_case':
      case 'screaming_snake_case':
        return '_';
      case 'kebab_case':
        return '-';
      case 'pascal_case':
      case 'camel_case':
        return '';
      default:
        return '-';
    }
  };

  const formatValue = (value: string, format: NamingConventionFormat, isFirstBlock: boolean = false): string => {
    switch (format) {
      case 'pascal_case':
        return value.split(/[-_\s]/).map(word => 
          word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join('');
      case 'camel_case':
        const words = value.split(/[-_\s]/);
        // First block: lowercase, subsequent blocks: capitalize first letter
        if (isFirstBlock) {
          return words[0].toLowerCase() + words.slice(1).map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join('');
        } else {
          // Subsequent blocks: capitalize first letter of each word
          return words.map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join('');
        }
      case 'snake_case':
        return value.toLowerCase().replace(/[-_\s]/g, '_');
      case 'kebab_case':
        return value.toLowerCase().replace(/[_\s]/g, '-');
      case 'screaming_snake_case':
        return value.toUpperCase().replace(/[-_\s]/g, '_');
      default:
        return value;
    }
  };

  const separator = getSeparator(format);
  const exampleValues: string[] = [];

  blockNames.forEach((blockName, index) => {
    const block = blockList[blockName];
    if (!block) return;

    let exampleValue = '';
    
    if (block.type === 'short_names') {
      // For short names block, use a realistic EC2 short name
      // Check if EC2 instance short name is available in the block
      if (block.short_names && block.short_names.ec2 && block.short_names.ec2.instance && block.short_names.ec2.instance.length > 0) {
        // Use the configured short name for EC2 instance
        const shortNameParts = block.short_names.ec2.instance;
        // Format according to naming convention
        exampleValue = shortNameParts.join('');
      } else {
        // Default EC2 instance short name (common default)
        exampleValue = 'ec2inst';
      }
    } else if (block.type === 'general') {
      // Generate example based on block configuration
      if (block.allowed_type === 'list_of_constants' && block.allowed_list_of_constants && block.allowed_list_of_constants.length > 0) {
        // Use first allowed constant
        exampleValue = block.allowed_list_of_constants[0];
      } else if (block.allowed_type === 'regex') {
        if (block.allowed_regex) {
          // Find an example that actually matches the allowed regex
          const candidates = ['123', '001', '01', '1', 'prod', 'web', 'app', 'dev', 'staging', 'a', 'Env1', '12345', 'app2'];
          try {
            const regex = new RegExp(block.allowed_regex);
            exampleValue = candidates.find((c) => regex.test(c)) ?? '…';
          } catch {
            exampleValue = '…';
          }
        } else {
          exampleValue = index === 0 ? 'prod' : index === 1 ? 'web' : `app${index}`;
        }
      } else if (block.denied_type === 'regex' && block.denied_regex) {
        // Find an example that does NOT match the denied regex
        const candidates = ['prod', 'web', 'app', '123', '001', 'dev', 'staging', '01', 'Env1'];
        try {
          const regex = new RegExp(block.denied_regex);
          exampleValue = candidates.find((c) => !regex.test(c)) ?? '…';
        } catch {
          exampleValue = 'prod';
        }
      } else if (block.denied_type === 'list_of_constants' && block.denied_list_of_constants && block.denied_list_of_constants.length > 0) {
        // If only denied type is set, use a generic value that wouldn't be denied
        if (index === 0) {
          exampleValue = 'prod';
        } else if (index === 1) {
          exampleValue = 'web';
        } else {
          exampleValue = `app${index}`;
        }
      } else {
        // Default examples for common block positions
        if (index === 0) {
          exampleValue = 'prod'; // Environment
        } else if (index === 1) {
          exampleValue = 'web'; // Service/application
        } else {
          exampleValue = `app${index}`;
        }
      }
    }

    if (exampleValue) {
      // For camel_case, first block should be lowercase, subsequent blocks should start with uppercase
      const isFirstBlock = index === 0;
      exampleValues.push(formatValue(exampleValue, format, isFirstBlock));
    }
  });

  let example = exampleValues.join(separator);

  // Add suffix
  if (suffix === 'unlimited_suffix') {
    // For unlimited suffix in example, show a few repeating numbers to indicate it can repeat
    const suffixSeparator = format === 'kebab_case' ? '-' : format === 'snake_case' || format === 'screaming_snake_case' ? '_' : '';
    // For camel case and pascal case, numbers are appended directly without separator
    if (format === 'camel_case' || format === 'pascal_case') {
      example += '001002...';
    } else {
      example += `${suffixSeparator}001${suffixSeparator}002${suffixSeparator}...`;
    }
  } else if (suffix === 'limited_suffix') {
    const repeatCount = limitedSuffixRepeatCount ?? 1;
    // Last structure block counts as first suffix block, so additional = repeatCount - 1
    const additionalCount = Math.max(0, repeatCount - 1);
    const suffixSeparator = format === 'kebab_case' ? '-' : format === 'snake_case' || format === 'screaming_snake_case' ? '_' : '';
    // Add blocks for each additional suffix count
    const suffixValues: string[] = [];
    for (let i = 0; i < additionalCount; i++) {
      // Format the number with leading zeros
      const num = String(i + 1).padStart(3, '0');
      // For camel case and pascal case, numbers are appended directly
      if (format === 'camel_case' || format === 'pascal_case') {
        suffixValues.push(num);
      } else {
        suffixValues.push(formatValue(num, format, false));
      }
    }
    // For camel case and pascal case, join without separator
    if (format === 'camel_case' || format === 'pascal_case') {
      example += suffixValues.join('');
    } else {
      example += suffixSeparator + suffixValues.join(suffixSeparator);
    }
  }

  return example;
};

// Helper function to get block description
const getBlockDescription = (block: NamingConventionBlock, blockName: string): string => {
  if (block.type === 'short_names') {
    return `Short Resource Type Block: Automatically uses the short name for the resource type (e.g., "i" for EC2 instances).`;
  }

  if (block.type === 'general') {
    const parts: string[] = [];
    
    if (block.allowed_type === 'list_of_constants' && block.allowed_list_of_constants && block.allowed_list_of_constants.length > 0) {
      parts.push(`Allows only: ${block.allowed_list_of_constants.slice(0, 3).join(', ')}${block.allowed_list_of_constants.length > 3 ? ` (+${block.allowed_list_of_constants.length - 3} more)` : ''}`);
    } else if (block.allowed_type === 'regex' && block.allowed_regex) {
      parts.push(`Allows pattern: ${block.allowed_regex}`);
    } else if (block.allowed_type) {
      parts.push(`Allows: ${block.allowed_type}`);
    }

    if (block.denied_type === 'list_of_constants' && block.denied_list_of_constants && block.denied_list_of_constants.length > 0) {
      parts.push(`Restricts: ${block.denied_list_of_constants.slice(0, 3).join(', ')}${block.denied_list_of_constants.length > 3 ? ` (+${block.denied_list_of_constants.length - 3} more)` : ''}`);
    } else if (block.denied_type === 'regex' && block.denied_regex) {
      parts.push(`Restricts pattern: ${block.denied_regex}`);
    } else if (block.denied_type) {
      parts.push(`Restricts: ${block.denied_type}`);
    }

    if (parts.length === 0) {
      return `General Block: Configure allowed or denied values to control what can appear in this position.`;
    }

    return `General Block: ${parts.join('. ')}.`;
  }

  return `Block: ${blockName}`;
};

// Helper function to parse name based on format
const parseNameByFormat = (name: string, format: NamingConventionFormat): string[] => {
  switch (format) {
    case 'pascal_case':
    case 'camel_case':
      // Split by capital letters
      return name.split(/(?=[A-Z])/).map(s => s.toLowerCase()).filter(s => s.length > 0);
    case 'snake_case':
    case 'screaming_snake_case':
      return name.split('_').map(s => s.trim().toLowerCase()).filter(s => s.length > 0);
    case 'kebab_case':
      return name.split('-').map(s => s.trim().toLowerCase()).filter(s => s.length > 0);
    default:
      return [name];
  }
};

// Helper to validate a single part against a block's rules (used for main blocks and suffix segments)
const validatePartAgainstBlock = (
  part: string,
  block: NamingConventionBlock,
  defaultShortNames?: Record<string, Record<string, string[]>>
): { valid: boolean; message: string } => {
  if (block.type === 'short_names') {
    let foundMatch = false;
    let blockMessage = '';
    if (defaultShortNames) {
      for (const [service, resourceTypes] of Object.entries(defaultShortNames)) {
        for (const [resourceType, shortNameParts] of Object.entries(resourceTypes)) {
          const shortName = shortNameParts.join('');
          if (part === shortName.toLowerCase() || part === shortName) {
            foundMatch = true;
            blockMessage = `Matches short name for ${service}/${resourceType}`;
            break;
          }
        }
        if (foundMatch) break;
      }
    }
    if (!foundMatch && block.short_names) {
      for (const [service, resourceTypes] of Object.entries(block.short_names)) {
        for (const [resourceType, shortNameParts] of Object.entries(resourceTypes)) {
          const shortName = shortNameParts.join('');
          if (part === shortName.toLowerCase() || part === shortName) {
            foundMatch = true;
            blockMessage = `Matches short name for ${service}/${resourceType}`;
            break;
          }
        }
        if (foundMatch) break;
      }
    }
    if (!foundMatch) blockMessage = `Does not match any configured short name (got: "${part}")`;
    return { valid: foundMatch, message: blockMessage };
  }

  // General block
  let allowedValid = false;
  let deniedValid = true;
  let blockMessage = '';
  const genBlock = block as NamingConventionGeneralBlock;

  if (genBlock.allowed_type === 'list_of_constants' && genBlock.allowed_list_of_constants) {
    const normalizedPart = part.toLowerCase();
    const normalizedConstants = genBlock.allowed_list_of_constants.map((c) => c.toLowerCase());
    if (normalizedConstants.includes(normalizedPart)) {
      allowedValid = true;
      blockMessage = `Matches allowed constant: "${part}"`;
    } else {
      blockMessage = `Not in allowed list (got: "${part}", expected one of: ${genBlock.allowed_list_of_constants.slice(0, 5).join(', ')}${genBlock.allowed_list_of_constants.length > 5 ? '...' : ''})`;
    }
  } else if (genBlock.allowed_type === 'regex' && genBlock.allowed_regex) {
    try {
      const regex = new RegExp(genBlock.allowed_regex);
      if (regex.test(part)) {
        allowedValid = true;
        blockMessage = `Matches allowed regex pattern: ${genBlock.allowed_regex}`;
      } else {
        blockMessage = `Does not match allowed regex pattern: ${genBlock.allowed_regex}`;
      }
    } catch {
      blockMessage = `Invalid regex pattern: ${genBlock.allowed_regex}`;
    }
  } else if (genBlock.allowed_type) {
    blockMessage = `Allowed type configured but no validation rule set`;
  } else {
    allowedValid = true;
    blockMessage = `No allowed restrictions (accepts any value)`;
  }

  if (genBlock.denied_type === 'list_of_constants' && genBlock.denied_list_of_constants) {
    const normalizedPart = part.toLowerCase();
    const normalizedConstants = genBlock.denied_list_of_constants.map((c) => c.toLowerCase());
    if (normalizedConstants.includes(normalizedPart)) {
      deniedValid = false;
      blockMessage = `Matches denied constant: "${part}"`;
    } else if (allowedValid) {
      blockMessage = `Matches allowed constant: "${part}" (not in denied list)`;
    }
  } else if (genBlock.denied_type === 'regex' && genBlock.denied_regex) {
    try {
      const regex = new RegExp(genBlock.denied_regex);
      if (regex.test(part)) {
        deniedValid = false;
        blockMessage = `Matches denied regex pattern: ${genBlock.denied_regex}`;
      } else if (allowedValid) {
        blockMessage = `Matches allowed regex pattern: ${genBlock.allowed_regex || 'any (not denied)'}`;
      }
    } catch {
      // Invalid regex, ignore
    }
  }

  const blockValid = allowedValid && deniedValid;
  if (!genBlock.allowed_type && !genBlock.denied_type) {
    return { valid: false, message: 'Block has no allowed or denied rules configured' };
  }
  return { valid: blockValid, message: blockMessage };
};

// Helper function to validate a name against naming convention
const validateNameAgainstConvention = (
  name: string,
  format: NamingConventionFormat,
  structure: NamingConventionStructureItem[],
  blockList: Record<string, NamingConventionBlock>,
  suffix: SuffixType,
  limitedSuffixRepeatCount?: number,
  defaultShortNames?: Record<string, Record<string, string[]>>
): { valid: boolean; message: string; details?: string[] } => {
  if (!name || name.trim() === '') {
    return { valid: false, message: 'Name cannot be empty' };
  }

  if (structure.length === 0) {
    return { valid: false, message: 'No blocks configured in naming convention' };
  }

  // Parse the name according to format
  const parts = parseNameByFormat(name, format);
  
  if (parts.length === 0) {
    return { valid: false, message: 'Could not parse name according to format', details: ['Name does not match the selected format'] };
  }

  // Validate format matches
  const getSeparator = (format: NamingConventionFormat): string => {
    switch (format) {
      case 'snake_case':
      case 'screaming_snake_case':
        return '_';
      case 'kebab_case':
        return '-';
      case 'pascal_case':
      case 'camel_case':
        return '';
      default:
        return '-';
    }
  };

  const separator = getSeparator(format);

  // Check if the name matches the expected format
  if (format === 'snake_case' && !/^[a-z0-9_]+$/.test(name)) {
    return { valid: false, message: 'Name does not match snake_case format', details: ['Expected lowercase letters, numbers, and underscores'] };
  }
  if (format === 'screaming_snake_case' && !/^[A-Z0-9_]+$/.test(name)) {
    return { valid: false, message: 'Name does not match SCREAMING_SNAKE_CASE format', details: ['Expected uppercase letters, numbers, and underscores'] };
  }
  if (format === 'kebab_case' && !/^[a-z0-9-]+$/.test(name)) {
    return { valid: false, message: 'Name does not match kebab-case format', details: ['Expected lowercase letters, numbers, and hyphens'] };
  }
  if (format === 'pascal_case' && !/^[A-Z][a-zA-Z0-9]*$/.test(name)) {
    return { valid: false, message: 'Name does not match PascalCase format', details: ['Expected first letter uppercase, followed by letters and numbers'] };
  }
  if (format === 'camel_case' && !/^[a-z][a-zA-Z0-9]*$/.test(name)) {
    return { valid: false, message: 'Name does not match camelCase format', details: ['Expected first letter lowercase, followed by letters and numbers'] };
  }

  // Validate each block in order
  // When suffix is enabled, the last block is part of the optional suffix
  const hasSuffix = suffix !== 'no_suffix';

  let currentPartIndex = 0;
  let lastBlockConsumedAsSuffix = false;
  const blockResults: string[] = [];

  for (let i = 0; i < structure.length; i++) {
    const structureItem = structure[i];
    const block = blockList[structureItem.block_name];
    const isLastBlock = i === structure.length - 1;
    const isOptionalSuffixBlock = hasSuffix && isLastBlock;
    
    if (!block) {
      blockResults.push(`Block ${i + 1} (${structureItem.block_name}): Block not found`);
      continue;
    }

    if (currentPartIndex >= parts.length) {
      if (isOptionalSuffixBlock) {
        // Last block is optional when suffix is enabled
        blockResults.push(`Block ${i + 1} (${structureItem.block_name}): [Optional suffix block - not present]`);
        continue;
      }
      blockResults.push(`Block ${i + 1} (${structureItem.block_name}): Missing required block`);
      return {
        valid: false,
        message: `Validation failed: missing required block ${i + 1} (${structureItem.block_name})`,
        details: blockResults
      };
    }

    const part = parts[currentPartIndex];
    const { valid: blockValid, message: blockMessage } = validatePartAgainstBlock(part, block, defaultShortNames);

    blockResults.push(`Block ${i + 1} (${structureItem.block_name}): ${blockValid ? '✓' : '✗'} ${blockMessage}`);
    
    if (blockValid) {
      currentPartIndex++;
      if (isOptionalSuffixBlock) {
        lastBlockConsumedAsSuffix = true;
      }
    } else {
      // Block validation failed
      if (isOptionalSuffixBlock) {
        // Last block is optional when suffix is enabled - validation can continue
        // The remaining parts will be treated as additional suffix blocks
        blockResults[blockResults.length - 1] = `Block ${i + 1} (${structureItem.block_name}): [Optional suffix block - skipped, treating remaining as suffix]`;
        break; // Exit the loop, don't fail
      }
      return {
        valid: false,
        message: `Validation failed at block ${i + 1}`,
        details: blockResults
      };
    }
  }

  // Check suffix
  // All suffix segments must match the rules of the last structure block
  const remainingParts = parts.slice(currentPartIndex);
  const lastStructureItem = structure[structure.length - 1];
  const lastBlock = lastStructureItem ? blockList[lastStructureItem.block_name] : null;

  if (suffix === 'unlimited_suffix') {
    // For unlimited suffix, any number of remaining parts allowed, each must match last block rules
    if (remainingParts.length === 0) {
      blockResults.push(`Suffix: no suffix blocks (all suffix blocks are optional)`);
      return { valid: true, message: 'Name is valid (no suffix)', details: blockResults };
    }
    if (!lastBlock) {
      blockResults.push(`Suffix: ${remainingParts.length} suffix block(s) found (cannot validate: last block not found)`);
      return { valid: true, message: 'Name is valid (with suffix)', details: blockResults };
    }
    for (let s = 0; s < remainingParts.length; s++) {
      const { valid: suffixValid, message: suffixMessage } = validatePartAgainstBlock(remainingParts[s], lastBlock, defaultShortNames);
      blockResults.push(`Suffix ${s + 1}: ${suffixValid ? '✓' : '✗'} ${suffixMessage}`);
      if (!suffixValid) {
        return {
          valid: false,
          message: `Suffix segment ${s + 1} does not match last block rules (${lastStructureItem.block_name})`,
          details: blockResults
        };
      }
    }
    return { valid: true, message: 'Name is valid (with suffix)', details: blockResults };
  } else if (suffix === 'limited_suffix') {
    const maxSuffixCount = limitedSuffixRepeatCount ?? 1;
    // Limited suffix: up to maxSuffixCount segments total, each must match last block rules
    // The last structure block (if consumed) counts as 1 suffix block; remainingParts are additional
    const totalSuffixBlocks = (lastBlockConsumedAsSuffix ? 1 : 0) + remainingParts.length;
    if (totalSuffixBlocks === 0) {
      blockResults.push(`Suffix: no suffix blocks (0 to ${maxSuffixCount} allowed)`);
      return { valid: true, message: 'Name is valid (no suffix)', details: blockResults };
    }
    if (totalSuffixBlocks > maxSuffixCount) {
      return {
        valid: false,
        message: `Suffix too long: maximum ${maxSuffixCount} suffix block(s) allowed, found ${totalSuffixBlocks}`,
        details: blockResults
      };
    }
    if (!lastBlock) {
      blockResults.push(`Suffix: ${remainingParts.length} suffix block(s) found (cannot validate: last block not found)`);
      return { valid: true, message: 'Name is valid (with suffix)', details: blockResults };
    }
    for (let s = 0; s < remainingParts.length; s++) {
      const { valid: suffixValid, message: suffixMessage } = validatePartAgainstBlock(remainingParts[s], lastBlock, defaultShortNames);
      blockResults.push(`Suffix ${s + 1}: ${suffixValid ? '✓' : '✗'} ${suffixMessage}`);
      if (!suffixValid) {
        return {
          valid: false,
          message: `Suffix segment ${s + 1} does not match last block rules (${lastStructureItem.block_name})`,
          details: blockResults
        };
      }
    }
    blockResults.push(`Suffix: ${totalSuffixBlocks} block(s) (0 to ${maxSuffixCount} allowed)`);
    return { valid: true, message: 'Name is valid (with suffix)', details: blockResults };
  } else {
    // No suffix
    if (currentPartIndex < parts.length) {
      const remainingParts = parts.slice(currentPartIndex);
      return {
        valid: false,
        message: `Extra parts found (no suffix allowed): ${remainingParts.join(separator)}`,
        details: blockResults
      };
    }
  }

  return { valid: true, message: 'Name is valid', details: blockResults };
};

export const NamingConventionRuleForm: React.FC<NamingConventionRuleFormProps> = ({
  open,
  onClose,
  onSuccess,
  mode,
  ruleName,
}) => {
  const { showNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingDefault, setLoadingDefault] = useState(false);
  const [formData, setFormData] = useState<{
    ruleName: string;
    configuration: NamingConventionConfiguration;
  }>({
    ruleName: '',
    configuration: {
      naming_convention_format: 'pascal_case',
      block_list: {},
      structure: [],
      suffix: 'no_suffix',
    },
  });
  const [defaultShortNames, setDefaultShortNames] = useState<Record<string, Record<string, string[]>>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [editingBlock, setEditingBlock] = useState<string | null>(null);
  const [showAddBlockDialog, setShowAddBlockDialog] = useState(false);
  const [addBlockType, setAddBlockType] = useState<BlockType | null>(null);
  const [addBlockDraft, setAddBlockDraft] = useState<{ blockName: string; block: NamingConventionBlock } | null>(null);
  const [showBlockDescriptions, setShowBlockDescriptions] = useState(false);
  const [testName, setTestName] = useState('');
  const [testResult, setTestResult] = useState<{ valid: boolean; message: string; details?: string[] } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Re-validate test name when naming convention configuration changes
  useEffect(() => {
    if (testName.trim()) {
      const result = validateNameAgainstConvention(
        testName.trim(),
        formData.configuration.naming_convention_format,
        formData.configuration.structure,
        formData.configuration.block_list,
        formData.configuration.suffix,
        formData.configuration.limited_suffix_repeat_count,
        defaultShortNames
      );
      setTestResult(result);
    }
  }, [
    testName,
    formData.configuration.naming_convention_format,
    formData.configuration.structure,
    formData.configuration.block_list,
    formData.configuration.suffix,
    formData.configuration.limited_suffix_repeat_count,
    defaultShortNames
  ]);

  const loadDefaultShortNames = useCallback(async () => {
    try {
      setLoadingDefault(true);
      const response = await tagopsService.getDefaultNamingConvention();
      // Response structure from API:
      // { success: true, data: { message: "...", data: { acm: {...}, ec2: {...}, ... } } }
      // After unwrapping by request method, response is: { message: "...", data: { acm: {...}, ec2: {...}, ... } }
      // So the short_names object (acm, ec2, etc.) is directly in response.data
      const shortNames = response.data || {};
      console.log('Loaded default short names:', shortNames, 'Full response:', response);
      if (Object.keys(shortNames).length === 0) {
        console.warn('No short names loaded from API response. You can still create a short names block and configure it manually.');
      }
      setDefaultShortNames(shortNames);
    } catch (error: any) {
      console.error('Error loading default short names:', error);
      // Don't show error notification - allow user to create block anyway
      // showNotification(createErrorNotification(error, undefined, 'Failed to load default short names'));
      setDefaultShortNames({}); // Set empty object so button can be enabled
    } finally {
      setLoadingDefault(false);
    }
  }, []);

  const loadRule = useCallback(async (name: string) => {
    try {
      setLoading(true);
      const response = await tagopsService.getNamingConvention(name);
      const config = response.ruleConfiguration;
      
      // Ensure structure matches block_list order (in case structure is out of sync)
      const blockNames = Object.keys(config.block_list);
      const structureBlockNames = config.structure.map(item => item.block_name);
      
      // If structure doesn't match block_list, regenerate it
      if (blockNames.length !== structureBlockNames.length || 
          !blockNames.every((name, index) => structureBlockNames[index] === name)) {
        config.structure = blockNames.map((blockName, index) => ({
          block_name: blockName,
          priority: index + 1,
        }));
      }
      
      setFormData({
        ruleName: name,
        configuration: config,
      });
      // Load default short names for reference
      await loadDefaultShortNames();
    } catch (error: any) {
      console.error('Error loading naming convention:', error);
      showNotification(createErrorNotification('Failed to load naming convention', error.message));
      onClose();
    } finally {
      setLoading(false);
    }
  }, [loadDefaultShortNames, onClose, showNotification]);

  const resetForm = useCallback(() => {
    setFormData({
      ruleName: '',
      configuration: {
        naming_convention_format: 'pascal_case',
        block_list: {},
        structure: [],
        suffix: 'no_suffix',
      },
    });
    setErrors({});
    setEditingBlock(null);
    setShowAddBlockDialog(false);
    setAddBlockType(null);
    setAddBlockDraft(null);
  }, []);

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && ruleName) {
        loadRule(ruleName);
      } else {
        resetForm();
        loadDefaultShortNames();
      }
    }
  }, [open, mode, ruleName, loadRule, loadDefaultShortNames, resetForm]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.ruleName.trim()) {
      newErrors.ruleName = 'Rule name is required';
    }

    if (Object.keys(formData.configuration.block_list).length === 0) {
      newErrors.block_list = 'At least one block is required';
    }

    // All blocks must have a user-provided name (no unnamed blocks)
    const unnamedBlocks = Object.keys(formData.configuration.block_list).filter((name) => name.startsWith('__new_'));
    if (unnamedBlocks.length > 0) {
      newErrors.block_list = newErrors.block_list || 'All blocks must have a name. Please name any unnamed blocks.';
    }

    // Structure is automatically generated from block list order, so validate it matches
    const blockNames = Object.keys(formData.configuration.block_list);
    if (formData.configuration.structure.length !== blockNames.length) {
      // Auto-fix: regenerate structure from block list
      const autoStructure = blockNames.map((blockName, index) => ({
        block_name: blockName,
        priority: index + 1,
      }));
      setFormData((prev) => ({
        ...prev,
        configuration: {
          ...prev.configuration,
          structure: autoStructure,
        },
      }));
    }

    // Validate structure items reference existing blocks
    for (const item of formData.configuration.structure) {
      if (!formData.configuration.block_list[item.block_name]) {
        newErrors.structure = `Block "${item.block_name}" in structure does not exist in block list`;
        break;
      }
    }

    // Validate general blocks have at least one of allowed_type or denied_type
    Object.entries(formData.configuration.block_list).forEach(([blockName, block]) => {
      if (block.type === 'general') {
        const hasAllowedType = !!block.allowed_type;
        const hasDeniedType = !!block.denied_type;
        if (!hasAllowedType && !hasDeniedType) {
          newErrors[`block_${blockName}`] = `Block "${blockName}" must have at least one of "Allowed Type" or "Denied Type" selected`;
        }
        // Validate required fields based on selected types
        if (hasAllowedType) {
          if (block.allowed_type === 'regex' && !block.allowed_regex) {
            newErrors[`block_${blockName}_allowed_regex`] = `Block "${blockName}": Allowed Regex is required when Allowed Type is Regex`;
          }
          if (block.allowed_type === 'list_of_constants' && (!block.allowed_list_of_constants || block.allowed_list_of_constants.length === 0)) {
            newErrors[`block_${blockName}_allowed_constants`] = `Block "${blockName}": At least one allowed constant is required`;
          }
        }
        if (hasDeniedType) {
          if (block.denied_type === 'regex' && !block.denied_regex) {
            newErrors[`block_${blockName}_denied_regex`] = `Block "${blockName}": Denied Regex is required when Denied Type is Regex`;
          }
          if (block.denied_type === 'list_of_constants' && (!block.denied_list_of_constants || block.denied_list_of_constants.length === 0)) {
            newErrors[`block_${blockName}_denied_constants`] = `Block "${blockName}": At least one denied constant is required when Denied Type is List of Constants`;
          }
        }
      } else if (block.type === 'short_names') {
        // Validate short_names block has required short_names property
        if (!block.short_names || Object.keys(block.short_names).length === 0) {
          newErrors[`block_${blockName}`] = `Block "${blockName}": Short names block must have at least one service configured`;
        }
      }
    });

    // Validate only one short_names block in structure
    const shortNamesBlocks = formData.configuration.structure.filter(
      (item) => formData.configuration.block_list[item.block_name]?.type === 'short_names'
    );
    if (shortNamesBlocks.length > 1) {
      newErrors.structure = 'Only one short_names block can be used in the structure';
    }

    // Validate limited_suffix_repeat_count (1-10 allowed)
    if (formData.configuration.suffix === 'limited_suffix') {
      const count = formData.configuration.limited_suffix_repeat_count;
      if (count === undefined || count === null) {
        newErrors.limited_suffix_repeat_count = 'Repeat count is required';
      } else if (count < 1 || count > 10) {
        newErrors.limited_suffix_repeat_count = 'Repeat count must be between 1 and 10';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Cleanup function to ensure schema compliance by removing undefined fields
  const cleanupConfiguration = (config: NamingConventionConfiguration): NamingConventionConfiguration => {
    const cleanedBlockList: Record<string, NamingConventionBlock> = {};
    
    Object.entries(config.block_list).forEach(([blockName, block]) => {
      if (block.type === 'general') {
        const cleanedBlock: NamingConventionGeneralBlock = {
          type: 'general',
        };
        
        // Only include allowed fields if allowed_type is set
        if (block.allowed_type) {
          cleanedBlock.allowed_type = block.allowed_type;
          if (block.allowed_type === 'regex' && block.allowed_regex) {
            cleanedBlock.allowed_regex = block.allowed_regex;
          } else if (block.allowed_type === 'list_of_constants' && block.allowed_list_of_constants) {
            cleanedBlock.allowed_list_of_constants = block.allowed_list_of_constants;
          }
        }
        
        // Only include denied fields if denied_type is set
        if (block.denied_type) {
          cleanedBlock.denied_type = block.denied_type;
          if (block.denied_type === 'regex' && block.denied_regex) {
            cleanedBlock.denied_regex = block.denied_regex;
          } else if (block.denied_type === 'list_of_constants' && block.denied_list_of_constants) {
            cleanedBlock.denied_list_of_constants = block.denied_list_of_constants;
          }
        }
        
        cleanedBlockList[blockName] = cleanedBlock;
      } else if (block.type === 'short_names') {
        // For short_names blocks, ensure short_names is present and not empty
        if (block.short_names && Object.keys(block.short_names).length > 0) {
          cleanedBlockList[blockName] = {
            type: 'short_names',
            short_names: block.short_names,
          };
        }
      }
    });
    
    const cleanedConfig: NamingConventionConfiguration = {
      naming_convention_format: config.naming_convention_format,
      block_list: cleanedBlockList,
      structure: config.structure,
      suffix: config.suffix,
    };
    
    // Only include limited_suffix_repeat_count if suffix is limited_suffix
    if (config.suffix === 'limited_suffix' && config.limited_suffix_repeat_count !== undefined && config.limited_suffix_repeat_count !== null) {
      cleanedConfig.limited_suffix_repeat_count = config.limited_suffix_repeat_count;
    }
    
    return cleanedConfig;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      // Cleanup configuration to ensure schema compliance
      const cleanedConfiguration = cleanupConfiguration(formData.configuration);
      
      if (mode === 'create') {
        await tagopsService.createNamingConvention(formData.ruleName, cleanedConfiguration);
        showNotification(createSuccessNotification('Success', 'Naming convention rule created successfully'));
      } else {
        await tagopsService.updateNamingConvention(formData.ruleName, cleanedConfiguration);
        showNotification(createSuccessNotification('Success', 'Naming convention rule updated successfully'));
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error saving naming convention:', error);
      showNotification(createErrorNotification(error, undefined, 'Failed to save naming convention'));
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAddBlockDialog = (blockType: BlockType) => {
    // Check if short_names block already exists
    if (blockType === 'short_names') {
      const hasShortNamesBlock = Object.values(formData.configuration.block_list).some(
        (block) => block.type === 'short_names'
      );
      if (hasShortNamesBlock) {
        showNotification(createErrorNotification(new Error('Short names block already exists'), undefined, 'Only one short names block is allowed per rule'));
        return;
      }
    }

    let newBlock: NamingConventionBlock;
    if (blockType === 'short_names') {
      const shortNamesCopy = Object.keys(defaultShortNames).length > 0
        ? JSON.parse(JSON.stringify(defaultShortNames))
        : {};
      newBlock = {
        type: 'short_names',
        short_names: shortNamesCopy,
      };
    } else {
      newBlock = {
        type: 'general',
      };
    }

    setAddBlockType(blockType);
    setAddBlockDraft({ blockName: '', block: newBlock });
    setShowAddBlockDialog(true);
  };

  const handleCloseAddBlockDialog = () => {
    setShowAddBlockDialog(false);
    setAddBlockType(null);
    setAddBlockDraft(null);
  };

  const handleAddBlockDraftBlockChange = useCallback((updatedBlock: NamingConventionBlock) => {
    setAddBlockDraft((prev) => prev ? { ...prev, block: updatedBlock } : prev);
  }, []);

  const handleSaveAddBlock = () => {
    if (!addBlockDraft || !addBlockType) return;

    const { blockName, block } = addBlockDraft;
    const trimmedName = blockName.trim();

    if (!trimmedName) {
      showNotification(createErrorNotification(new Error('Block name cannot be empty'), undefined, 'Block name is required'));
      return;
    }
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      showNotification(createErrorNotification(new Error('Invalid block name'), undefined, 'Block name must start with a letter or underscore, followed by letters, numbers, or underscores'));
      return;
    }
    if (formData.configuration.block_list[trimmedName]) {
      showNotification(createErrorNotification(new Error('Block name already exists'), undefined, 'A block with this name already exists'));
      return;
    }

    // Validate general block config
    if (block.type === 'general') {
      const b = block as NamingConventionGeneralBlock;
      if (!b.allowed_type && !b.denied_type) {
        showNotification(createErrorNotification(new Error('At least one of "Allowed Type" or "Denied Type" must be selected'), undefined, 'Select either Allowed Type or Denied Type (or both) to continue'));
        return;
      }
      if (b.allowed_type === 'regex' && (!b.allowed_regex || !b.allowed_regex.trim())) {
        showNotification(createErrorNotification(new Error('Allowed Regex is required'), undefined, 'Enter a regex pattern for Allowed Type'));
        return;
      }
      if (b.allowed_type === 'list_of_constants' && (!b.allowed_list_of_constants || b.allowed_list_of_constants.length === 0)) {
        showNotification(createErrorNotification(new Error('At least one allowed constant is required'), undefined, 'Add at least one constant for Allowed Type'));
        return;
      }
      if (b.denied_type === 'regex' && (!b.denied_regex || !b.denied_regex.trim())) {
        showNotification(createErrorNotification(new Error('Denied Regex is required'), undefined, 'Enter a regex pattern for Denied Type'));
        return;
      }
      if (b.denied_type === 'list_of_constants' && (!b.denied_list_of_constants || b.denied_list_of_constants.length === 0)) {
        showNotification(createErrorNotification(new Error('At least one denied constant is required'), undefined, 'Add at least one constant for Denied Type'));
        return;
      }
    }

    // Validate short_names block
    if (block.type === 'short_names') {
      const sBlock = block as NamingConventionShortNamesBlock;
      if (!sBlock.short_names || Object.keys(sBlock.short_names).length === 0) {
        showNotification(createErrorNotification(new Error('Short names block must have at least one service configured'), undefined, 'Configure at least one service'));
        return;
      }
    }

    const newPriority = Object.keys(formData.configuration.block_list).length + 1;
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        block_list: {
          ...prev.configuration.block_list,
          [trimmedName]: block,
        },
        structure: [
          ...prev.configuration.structure,
          { block_name: trimmedName, priority: newPriority },
        ],
      },
    }));
    handleCloseAddBlockDialog();
  };

  const handleDeleteBlock = (blockName: string) => {
    // Remove from block_list
    const { [blockName]: removed, ...remainingBlocks } = formData.configuration.block_list;
    
    // Remove from structure and regenerate based on remaining block order
    const remainingBlockNames = Object.keys(remainingBlocks);
    const updatedStructure = remainingBlockNames.map((name, index) => ({
      block_name: name,
      priority: index + 1,
    }));

    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        block_list: remainingBlocks,
        structure: updatedStructure,
      },
    }));

    if (editingBlock === blockName) {
      setEditingBlock(null);
    }
  };

  const handleUpdateBlock = (blockName: string, block: NamingConventionBlock) => {
    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        block_list: {
          ...prev.configuration.block_list,
          [blockName]: block,
        },
      },
    }));
  };

  const handleRenameBlock = (oldBlockName: string, newBlockName: string, blockData: NamingConventionBlock) => {
    // Validate new block name
    if (!newBlockName || newBlockName.trim() === '') {
      showNotification(createErrorNotification(new Error('Block name cannot be empty'), undefined, 'Block name is required'));
      return false;
    }

    const trimmedName = newBlockName.trim();

    // If name didn't actually change, just return success
    if (trimmedName === oldBlockName) {
      return true;
    }

    // Check if name already exists (and it's not the same block)
    if (formData.configuration.block_list[trimmedName]) {
      showNotification(createErrorNotification(new Error('Block name already exists'), undefined, 'A block with this name already exists'));
      return false;
    }

    // Validate block name format (must start with letter or underscore, followed by letters, numbers, or underscores)
    // Schema pattern: ^[a-zA-Z_][a-zA-Z0-9_]*$
    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmedName)) {
      showNotification(createErrorNotification(new Error('Invalid block name'), undefined, 'Block name must start with a letter or underscore, followed by letters, numbers, or underscores'));
      return false;
    }

    // Get the block data (use the updated blockData passed in, not the old one from block_list)
    const block = blockData;

    // Rename the block: remove old name, add with new name
    // Preserve the order by rebuilding the block_list
    const blockNames = Object.keys(formData.configuration.block_list);
    const updatedBlockList: Record<string, NamingConventionBlock> = {};
    blockNames.forEach((name) => {
      if (name === oldBlockName) {
        updatedBlockList[trimmedName] = block;
      } else {
        updatedBlockList[name] = formData.configuration.block_list[name];
      }
    });

    // Update structure references: if old block was in structure, update to new name
    const updatedStructure = formData.configuration.structure.map((item) =>
      item.block_name === oldBlockName
        ? { ...item, block_name: trimmedName }
        : item
    );

    setFormData((prev) => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        block_list: updatedBlockList,
        structure: updatedStructure,
      },
    }));

    // Switch to editing the renamed block
    setEditingBlock(trimmedName);

    return true;
  };

  const handleBlockListDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const blockNames = Object.keys(formData.configuration.block_list);
      const oldIndex = blockNames.indexOf(active.id as string);
      const newIndex = blockNames.indexOf(over.id as string);

      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedBlockNames = arrayMove(blockNames, oldIndex, newIndex);
        
        // Reorder block_list
        const reorderedBlockList: Record<string, NamingConventionBlock> = {};
        reorderedBlockNames.forEach((blockName) => {
          reorderedBlockList[blockName] = formData.configuration.block_list[blockName];
        });

        // Update structure automatically based on block list order
        const updatedStructure = reorderedBlockNames.map((blockName, index) => ({
          block_name: blockName,
          priority: index + 1,
        }));

        setFormData((prev) => ({
          ...prev,
          configuration: {
            ...prev.configuration,
            block_list: reorderedBlockList,
            structure: updatedStructure,
          },
        }));
      }
    }
  };


  const isAddBlockDraftValid = (): boolean => {
    if (!addBlockDraft) return false;
    const { blockName, block } = addBlockDraft;
    const trimmed = blockName.trim();
    if (!trimmed || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(trimmed)) return false;
    if (formData.configuration.block_list[trimmed]) return false;
    if (block.type === 'general') {
      const b = block as NamingConventionGeneralBlock;
      if (!b.allowed_type && !b.denied_type) return false;
      if (b.allowed_type === 'regex' && (!b.allowed_regex || !b.allowed_regex.trim())) return false;
      if (b.allowed_type === 'list_of_constants' && (!b.allowed_list_of_constants || b.allowed_list_of_constants.length === 0)) return false;
      if (b.denied_type === 'regex' && (!b.denied_regex || !b.denied_regex.trim())) return false;
      if (b.denied_type === 'list_of_constants' && (!b.denied_list_of_constants || b.denied_list_of_constants.length === 0)) return false;
    }
    if (block.type === 'short_names') {
      const s = block as NamingConventionShortNamesBlock;
      if (!s.short_names || Object.keys(s.short_names).length === 0) return false;
    }
    return true;
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {mode === 'create' ? 'Create Naming Convention Rule' : 'Edit Naming Convention Rule'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {/* Rule Name */}
            <TextField
              label="Rule Name"
              value={formData.ruleName}
              onChange={(e) => setFormData((prev) => ({ ...prev, ruleName: e.target.value }))}
              error={!!errors.ruleName}
              helperText={errors.ruleName}
              required
              disabled={mode === 'edit'}
              fullWidth
            />

            {/* Naming Convention Format */}
            <FormControl fullWidth required>
              <InputLabel>Naming Convention Format</InputLabel>
              <Select
                value={formData.configuration.naming_convention_format}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    configuration: {
                      ...prev.configuration,
                      naming_convention_format: e.target.value as NamingConventionFormat,
                    },
                  }))
                }
                label="Naming Convention Format"
              >
                {FORMAT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Block List */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Block List (Drag to reorder)</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenAddBlockDialog('general')}
                    size="small"
                  >
                    Add General Block
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      if (Object.keys(defaultShortNames).length === 0) {
                        showNotification(createErrorNotification(new Error('Default short names not loaded'), undefined, 'Please wait for short names to load, or refresh the page.'));
                        return;
                      }
                      handleOpenAddBlockDialog('short_names');
                    }}
                    size="small"
                    disabled={loadingDefault || Object.values(formData.configuration.block_list).some((block) => block.type === 'short_names')}
                    title={
                      loadingDefault 
                        ? 'Loading default short names...' 
                        : Object.values(formData.configuration.block_list).some((block) => block.type === 'short_names')
                        ? 'Only one short names block is allowed'
                        : 'Add a block that uses short names for resource types (e.g., "i" for EC2 instances)'
                    }
                  >
                    Add Resource Type Short Name Block
                  </Button>
                </Box>
              </Box>
              {errors.block_list && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.block_list}
                </Alert>
              )}
              {Object.keys(formData.configuration.block_list).length === 0 ? (
                <Alert severity="info">
                  No blocks added yet. Add blocks to build your naming convention.
                </Alert>
              ) : (
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleBlockListDragEnd}>
                  <SortableContext
                    items={Object.keys(formData.configuration.block_list)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, flexWrap: 'wrap' }}>
                      {Object.entries(formData.configuration.block_list).map(([blockName, block]) => {
                        const blockError = errors[`block_${blockName}`];
                        const allowedRegexError = errors[`block_${blockName}_allowed_regex`];
                        const allowedConstantsError = errors[`block_${blockName}_allowed_constants`];
                        const deniedRegexError = errors[`block_${blockName}_denied_regex`];
                        const deniedConstantsError = errors[`block_${blockName}_denied_constants`];
                        return (
                          <Box key={blockName} sx={{ minWidth: 200, maxWidth: 280, flex: '0 0 auto' }}>
                            {(blockError || allowedRegexError || allowedConstantsError || deniedRegexError || deniedConstantsError) && (
                              <Alert severity="error" sx={{ mb: 1 }}>
                                {blockError || allowedRegexError || allowedConstantsError || deniedRegexError || deniedConstantsError}
                              </Alert>
                            )}
                            <SortableBlockItem
                              id={blockName}
                              blockName={blockName}
                              block={block}
                              defaultShortNames={defaultShortNames}
                              namingConventionFormat={formData.configuration.naming_convention_format}
                              onUpdate={(updatedBlock) => handleUpdateBlock(blockName, updatedBlock)}
                              onRename={(newName, blockData) => handleRenameBlock(blockName, newName, blockData)}
                              onDelete={() => handleDeleteBlock(blockName)}
                              isEditing={editingBlock === blockName}
                              onEdit={() => setEditingBlock(blockName)}
                              onCloseEdit={() => setEditingBlock(null)}
                              isNewBlock={blockName.startsWith('__new_')}
                            />
                          </Box>
                        );
                      })}
                    </Box>
                  </SortableContext>
                </DndContext>
              )}
              
              {/* Naming Convention Preview - hide when editing or adding a block to avoid layout flash */}
              {formData.configuration.structure.length > 0 && !editingBlock && !showAddBlockDialog && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Naming Convention Preview:
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => setShowBlockDescriptions(!showBlockDescriptions)}
                      variant="outlined"
                    >
                      {showBlockDescriptions ? 'Hide' : 'Show'} Block Details
                    </Button>
                  </Box>
                  
                  {/* Template Preview */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Template:
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      {(() => {
                        const preview = generateNamingConventionPreview(
                          formData.configuration.naming_convention_format,
                          formData.configuration.structure.map((item) =>
                            item.block_name.startsWith('__new_') ? 'Block' : item.block_name
                          ),
                          formData.configuration.suffix,
                          formData.configuration.limited_suffix_repeat_count
                        );
                        return (
                          <>
                            <Typography 
                              component="span"
                              sx={{ 
                                fontFamily: 'monospace', 
                                fontSize: '1.1rem',
                                color: 'primary.main',
                                fontWeight: 500
                              }}
                            >
                              {preview.main}
                            </Typography>
                            {preview.suffix && (
                              <Tooltip
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                      Suffix Section
                                    </Typography>
                                    <Typography variant="caption" component="div">
                                      {formData.configuration.suffix === 'unlimited_suffix' 
                                        ? 'All suffix blocks are optional. You can have 0 or more suffix blocks, with no maximum limit.'
                                        : `All suffix blocks are optional. You can have 0 to ${formData.configuration.limited_suffix_repeat_count ?? 1} suffix block(s).`
                                      }
                                    </Typography>
                                    <Typography variant="caption" component="div" sx={{ mt: 0.5, fontStyle: 'italic' }}>
                                      All blocks shown in [brackets] are optional and can be omitted.
                                    </Typography>
                                  </Box>
                                }
                                arrow
                                placement="top"
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    bgcolor: 'action.hover',
                                    borderRadius: 1,
                                    px: 1,
                                    py: 0.5,
                                    ml: 0.5,
                                    border: '1px dashed',
                                    borderColor: 'primary.main',
                                    cursor: 'help',
                                  }}
                                >
                                  <Typography 
                                    component="span"
                                    sx={{ 
                                      fontFamily: 'monospace', 
                                      fontSize: '1.1rem',
                                      color: 'primary.main',
                                      fontWeight: 500
                                    }}
                                  >
                                    {preview.suffix}
                                  </Typography>
                                  <Chip 
                                    label="Suffix" 
                                    size="small" 
                                    color="primary" 
                                    variant="outlined"
                                    sx={{ ml: 1, height: 20, fontSize: '0.65rem' }}
                                  />
                                </Box>
                              </Tooltip>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* EC2 Instance Example */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Example (EC2 Instance):
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontSize: '1.1rem',
                        color: 'success.main',
                        fontWeight: 500
                      }}
                    >
                      {generateEC2Example(
                        formData.configuration.naming_convention_format,
                        formData.configuration.structure.map((item) => item.block_name),
                        formData.configuration.block_list,
                        formData.configuration.suffix,
                        formData.configuration.limited_suffix_repeat_count
                      )}
                    </Typography>
                  </Box>

                  {/* Block Descriptions */}
                  {showBlockDescriptions && (
                    <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block', fontWeight: 600 }}>
                        Block Details:
                      </Typography>
                      {formData.configuration.structure.map((item, index) => {
                        const block = formData.configuration.block_list[item.block_name];
                        if (!block) return null;
                        return (
                          <Box key={item.block_name} sx={{ mb: 1.5 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                              Block {index + 1}: {item.block_name.startsWith('__new_') ? 'Unnamed' : item.block_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.75rem' }}>
                              {getBlockDescription(block, item.block_name)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Blocks are arranged in the order shown above. Drag blocks to reorder.
                    {formData.configuration.suffix === 'unlimited_suffix' && ` The suffix section (shown in brackets) is entirely optional - you can have 0 or more suffix blocks.`}
                    {formData.configuration.suffix === 'limited_suffix' && ` The suffix section (shown in brackets) is entirely optional - you can have 0 to ${formData.configuration.limited_suffix_repeat_count ?? 1} suffix block(s).`}
                  </Typography>
                </Box>
              )}
            </Box>


            {/* Naming Convention Tester */}
            {formData.configuration.structure.length > 0 && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                  Test Naming Convention
                </Typography>
                <TextField
                  label="Enter a name to test"
                  value={testName}
                  onChange={(e) => {
                    setTestName(e.target.value);
                    if (e.target.value.trim()) {
                      const result = validateNameAgainstConvention(
                        e.target.value.trim(),
                        formData.configuration.naming_convention_format,
                        formData.configuration.structure,
                        formData.configuration.block_list,
                        formData.configuration.suffix,
                        formData.configuration.limited_suffix_repeat_count,
                        defaultShortNames
                      );
                      setTestResult(result);
                    } else {
                      setTestResult(null);
                    }
                  }}
                  placeholder="e.g., prod-web-ec2inst-001"
                  fullWidth
                  sx={{ mb: 2 }}
                />
                {testResult && (
                  <Box>
                    <Alert 
                      severity={testResult.valid ? 'success' : 'error'}
                      sx={{ mb: testResult.details && testResult.details.length > 0 ? 1 : 0 }}
                    >
                      {testResult.message}
                    </Alert>
                    {testResult.details && testResult.details.length > 0 && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'background.paper', borderRadius: 1, maxHeight: 200, overflowY: 'auto' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                          Validation Details:
                        </Typography>
                        {testResult.details.map((detail, index) => (
                          <Typography key={index} variant="caption" sx={{ display: 'block', mb: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* Suffix Configuration */}
            <Box>
              <FormControl component="fieldset">
                <FormLabel component="legend">Suffix Configuration</FormLabel>
                <RadioGroup
                  value={formData.configuration.suffix}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        suffix: e.target.value as SuffixType,
                        ...(e.target.value !== 'limited_suffix'
                          ? { limited_suffix_repeat_count: undefined }
                          : { limited_suffix_repeat_count: prev.configuration.limited_suffix_repeat_count ?? 1 }),
                      },
                    }))
                  }
                >
                  {SUFFIX_OPTIONS.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
              {formData.configuration.suffix === 'limited_suffix' && (
                <TextField
                  label="Repeat Count"
                  type="number"
                  value={formData.configuration.limited_suffix_repeat_count ?? ''}
                  onChange={(e) => {
                    const raw = parseInt(e.target.value, 10);
                    const val = isNaN(raw) ? undefined : Math.min(10, Math.max(1, raw));
                    setFormData((prev) => ({
                      ...prev,
                      configuration: {
                        ...prev.configuration,
                        limited_suffix_repeat_count: val,
                      },
                    }));
                  }}
                  error={!!errors.limited_suffix_repeat_count}
                  helperText={errors.limited_suffix_repeat_count || 'Maximum number of suffix blocks allowed (1–10)'}
                  required
                  inputProps={{ min: 1, max: 10 }}
                  sx={{ mt: 2, width: 200 }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Add Block Dialog - nested inside main dialog for proper stacking */}
        <Dialog
          open={showAddBlockDialog}
          onClose={handleCloseAddBlockDialog}
          maxWidth="md"
          fullWidth={false}
          TransitionProps={{ timeout: 150 }}
          PaperProps={{ sx: { maxHeight: '90vh', minWidth: 420 } }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {addBlockType === 'short_names' ? 'Add Resource Type Short Name Block' : 'Add General Block'}
              </Typography>
              <IconButton onClick={handleCloseAddBlockDialog} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ overflowY: 'auto' }}>
            {addBlockDraft && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
                <TextField
                  label="Block Name"
                  value={addBlockDraft.blockName}
                  onChange={(e) => setAddBlockDraft((prev) => prev ? { ...prev, blockName: e.target.value } : prev)}
                  fullWidth
                  required
                  placeholder="Enter block name (e.g. Environment, Service)"
                  helperText="Block name must start with a letter or underscore, followed by letters, numbers, or underscores"
                  error={!addBlockDraft.blockName.trim() || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(addBlockDraft.blockName)}
                />
                <BlockForm
                  block={addBlockDraft.block}
                  defaultShortNames={defaultShortNames}
                  namingConventionFormat={formData.configuration.naming_convention_format}
                  onChange={handleAddBlockDraftBlockChange}
                  onShortNamesChangesDetected={undefined}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddBlockDialog}>Cancel</Button>
            <Tooltip title={!isAddBlockDraftValid() ? 'Complete block name and configuration to save' : ''} arrow>
              <span>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleSaveAddBlock}
                  disabled={!addBlockDraft || !isAddBlockDraftValid()}
                >
                  Save
                </Button>
              </span>
            </Tooltip>
          </DialogActions>
        </Dialog>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? <CircularProgress size={20} /> : mode === 'create' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>

    </>
  );
};

// Sortable Block Item Component
interface SortableBlockItemProps {
  id: string;
  blockName: string;
  block: NamingConventionBlock;
  defaultShortNames: Record<string, Record<string, string[]>>;
  namingConventionFormat: NamingConventionFormat;
  onUpdate: (block: NamingConventionBlock) => void;
  onRename: (newName: string, blockData: NamingConventionBlock) => boolean;
  onDelete: () => void;
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
  isNewBlock?: boolean;
}

const SortableBlockItem: React.FC<SortableBlockItemProps> = ({
  id,
  blockName,
  block,
  defaultShortNames,
  namingConventionFormat,
  onUpdate,
  onRename,
  onDelete,
  isEditing,
  onEdit,
  onCloseEdit,
  isNewBlock = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <BlockEditor
        blockName={blockName}
        block={block}
        defaultShortNames={defaultShortNames}
        namingConventionFormat={namingConventionFormat}
        onUpdate={onUpdate}
        onRename={onRename}
        onDelete={onDelete}
        isEditing={isEditing}
        onEdit={onEdit}
        onCloseEdit={onCloseEdit}
        dragHandleProps={{ ...attributes, ...listeners }}
        isNewBlock={isNewBlock}
      />
    </div>
  );
};

// Block Editor Component
interface BlockEditorProps {
  blockName: string;
  block: NamingConventionBlock;
  defaultShortNames: Record<string, Record<string, string[]>>;
  namingConventionFormat: NamingConventionFormat;
  onUpdate: (block: NamingConventionBlock) => void;
  onRename: (newName: string, blockData: NamingConventionBlock) => boolean;
  onDelete: () => void;
  isEditing: boolean;
  onEdit: () => void;
  onCloseEdit: () => void;
  dragHandleProps?: any;
  isNewBlock?: boolean;
}

const BlockEditor: React.FC<BlockEditorProps> = ({
  blockName,
  block,
  defaultShortNames,
  namingConventionFormat,
  onUpdate,
  onRename,
  onDelete,
  isEditing,
  onEdit,
  onCloseEdit,
  dragHandleProps,
  isNewBlock = false,
}) => {
  const { showNotification } = useNotifications();

  // Deep copy block to avoid mutating the original
  const getInitialBlock = (blockToCopy: NamingConventionBlock) => {
    if (blockToCopy.type === 'short_names') {
      // For short_names blocks, preserve stored values if they exist
      return {
        ...blockToCopy,
        short_names: blockToCopy.short_names && Object.keys(blockToCopy.short_names).length > 0
          ? JSON.parse(JSON.stringify(blockToCopy.short_names))
          : undefined, // Will be set by ShortNamesBlockForm using defaults
      };
    }
    return JSON.parse(JSON.stringify(blockToCopy));
  };

  const [localBlock, setLocalBlock] = useState<NamingConventionBlock>(getInitialBlock(block));
  const [localBlockName, setLocalBlockName] = useState<string>(isNewBlock ? '' : blockName);
  const [shortNamesChanges, setShortNamesChanges] = useState<Array<{ service: string; resourceType: string; from: string; to: string }>>([]);

  const handleShortNamesChangesDetected = useCallback((changes: Array<{ service: string; resourceType: string; from: string; to: string }>) => {
    setShortNamesChanges(changes);
  }, []);

  useEffect(() => {
    // Reset local state when block changes or when we start/stop editing
    setLocalBlock(getInitialBlock(block));
    setLocalBlockName(isNewBlock ? '' : blockName);
    setShortNamesChanges([]); // Reset changes when switching blocks
  }, [block, blockName, isEditing, isNewBlock]);

  const getGeneralBlockValidationError = (): string | null => {
    if (localBlock.type !== 'general') return null;
    const b = localBlock as NamingConventionGeneralBlock;
    const hasAllowed = !!b.allowed_type;
    const hasDenied = !!b.denied_type;
    if (!hasAllowed && !hasDenied) {
      return 'Select at least one of "Allowed Type" or "Denied Type"';
    }
    if (hasAllowed) {
      if (b.allowed_type === 'regex' && (!b.allowed_regex || !b.allowed_regex.trim())) {
        return 'Allowed Regex is required when Allowed Type is Regex';
      }
      if (b.allowed_type === 'list_of_constants' && (!b.allowed_list_of_constants || b.allowed_list_of_constants.length === 0)) {
        return 'At least one allowed constant is required';
      }
    }
    if (hasDenied) {
      if (b.denied_type === 'regex' && (!b.denied_regex || !b.denied_regex.trim())) {
        return 'Denied Regex is required when Denied Type is Regex';
      }
      if (b.denied_type === 'list_of_constants' && (!b.denied_list_of_constants || b.denied_list_of_constants.length === 0)) {
        return 'At least one denied constant is required';
      }
    }
    return null;
  };

  const generalBlockValidationError = getGeneralBlockValidationError();
  const isGeneralBlockInvalid = !!generalBlockValidationError;

  const handleSave = () => {
    if (isGeneralBlockInvalid) {
      showNotification(createErrorNotification(
        new Error(generalBlockValidationError || 'Block configuration is incomplete'),
        undefined,
        generalBlockValidationError || 'Complete the block configuration to save'
      ));
      return;
    }

    // Always update the block data first to ensure any changes are saved
    onUpdate(localBlock);

    // New blocks require a user-provided name
    if (isNewBlock) {
      if (!localBlockName.trim()) {
        onRename('', localBlock);
        return;
      }
      const renameSuccess = onRename(localBlockName.trim(), localBlock);
      if (!renameSuccess) return;
    } else if (localBlockName.trim() !== blockName && localBlockName.trim() !== '') {
      const renameSuccess = onRename(localBlockName.trim(), localBlock);
      if (!renameSuccess) return;
    }

    onCloseEdit();
  };

  return (
    <>
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {dragHandleProps && (
              <Box {...dragHandleProps} sx={{ display: 'flex', alignItems: 'center', cursor: 'grab', '&:active': { cursor: 'grabbing' }, flexShrink: 0 }}>
                <DragIndicatorIcon color="action" fontSize="small" />
              </Box>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap color={isNewBlock ? 'text.secondary' : 'inherit'}>
                {isNewBlock ? '(Unnamed)' : blockName}
              </Typography>
              <Chip
                label={block.type === 'general' ? 'General' : 'Short Names'}
                size="small"
                color={block.type === 'short_names' ? 'primary' : 'default'}
                sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <IconButton size="small" onClick={onEdit} sx={{ padding: 0.5 }}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onDelete} color="error" sx={{ padding: 0.5 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Paper>
      
      {/* Block Edit Dialog - md size to avoid flash when opening (parent is lg) */}
      <Dialog 
        open={isEditing} 
        onClose={onCloseEdit} 
        maxWidth="md"
        fullWidth={false}
        TransitionProps={{ timeout: 150 }}
        PaperProps={{
          sx: { maxHeight: '90vh', minWidth: 420 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {isNewBlock ? 'Add Block' : `Edit Block: ${blockName}`}
            </Typography>
            <IconButton onClick={onCloseEdit} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            {localBlock.type === 'short_names' && shortNamesChanges.length > 0 && (
              <Paper 
                sx={{ 
                  p: 1.5, 
                  bgcolor: 'info.light', 
                  border: '1px solid', 
                  borderColor: 'info.main',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Changes ({shortNamesChanges.length}):
                </Typography>
                <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                  {shortNamesChanges.slice(0, 8).map((change, idx) => (
                    <Typography key={idx} variant="caption" sx={{ display: 'block', fontSize: '0.75rem', mb: 0.5 }}>
                      <strong>{change.service}/{change.resourceType}:</strong> {change.from} → {change.to}
                    </Typography>
                  ))}
                  {shortNamesChanges.length > 8 && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', fontStyle: 'italic', color: 'text.secondary' }}>
                      ...and {shortNamesChanges.length - 8} more
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}
            <TextField
              label="Block Name"
              value={localBlockName}
              onChange={(e) => setLocalBlockName(e.target.value)}
              fullWidth
              required
              placeholder={isNewBlock ? 'Enter block name (e.g. Environment, Service)' : undefined}
              helperText="Block name must start with a letter or underscore, followed by letters, numbers, or underscores"
              error={localBlockName.trim() === '' || !/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(localBlockName)}
            />
            <BlockForm
              block={localBlock}
              defaultShortNames={defaultShortNames}
              namingConventionFormat={namingConventionFormat}
              onChange={setLocalBlock}
              onShortNamesChangesDetected={localBlock.type === 'short_names' ? handleShortNamesChangesDetected : undefined}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseEdit}>
            Cancel
          </Button>
          <Tooltip
            title={isGeneralBlockInvalid ? generalBlockValidationError || 'Complete the block configuration to save' : ''}
            arrow
          >
            <span>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={isGeneralBlockInvalid}
              >
                Save
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Block Form Component
interface BlockFormProps {
  block: NamingConventionBlock;
  defaultShortNames: Record<string, Record<string, string[]>>;
  namingConventionFormat: NamingConventionFormat;
  onChange: (block: NamingConventionBlock) => void;
  onShortNamesChangesDetected?: (changes: Array<{ service: string; resourceType: string; from: string; to: string }>) => void;
}

const BlockForm: React.FC<BlockFormProps> = React.memo(({ block, defaultShortNames, namingConventionFormat, onChange, onShortNamesChangesDetected }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {block.type === 'general' ? (
        <GeneralBlockForm block={block} onChange={onChange} />
      ) : (
        <ShortNamesBlockForm 
          block={block} 
          defaultShortNames={defaultShortNames} 
          namingConventionFormat={namingConventionFormat}
          onChange={onChange}
          onChangesDetected={onShortNamesChangesDetected}
        />
      )}
    </Box>
  );
});

// General Block Form
interface GeneralBlockFormProps {
  block: NamingConventionGeneralBlock;
  onChange: (block: NamingConventionGeneralBlock) => void;
}

const GeneralBlockForm: React.FC<GeneralBlockFormProps> = ({ block, onChange }) => {
  const hasAllowedType = !!block.allowed_type;
  const hasDeniedType = !!block.denied_type;
  const showValidationError = !hasAllowedType && !hasDeniedType;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {showValidationError && (
        <Alert severity="error">
          At least one of &quot;Allowed Type&quot; or &quot;Denied Type&quot; must be selected before saving.
        </Alert>
      )}

      <FormControl fullWidth>
        <InputLabel>Allowed Type (Optional)</InputLabel>
        <Select
          value={block.allowed_type || ''}
          onChange={(e) => {
            const newValue = e.target.value as AllowedType | '';
            if (newValue === '') {
              onChange({
                ...block,
                allowed_type: undefined as any,
                allowed_regex: undefined,
                allowed_list_of_constants: undefined,
              });
            } else {
              onChange({
                ...block,
                allowed_type: newValue as AllowedType,
                ...(newValue === 'regex' && { allowed_list_of_constants: undefined }),
                ...(newValue === 'list_of_constants' && { allowed_regex: undefined }),
              });
            }
          }}
          label="Allowed Type (Optional)"
        >
          <MenuItem value="">Not Selected</MenuItem>
          <MenuItem value="regex">Regex</MenuItem>
          <MenuItem value="list_of_constants">List of Constants</MenuItem>
        </Select>
      </FormControl>

      {block.allowed_type === 'regex' && (
        <TextField
          label="Allowed Regex"
          value={block.allowed_regex || ''}
          onChange={(e) => onChange({ ...block, allowed_regex: e.target.value })}
          required
          fullWidth
          helperText="Regular expression pattern for allowed values"
        />
      )}

      {block.allowed_type === 'list_of_constants' && (
        <>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={Array.isArray(block.allowed_list_of_constants) ? block.allowed_list_of_constants : []}
            onChange={(event, newValue) => {
              onChange({
                ...block,
                allowed_list_of_constants: newValue,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Allowed Constants"
                placeholder="Enter constants"
                helperText="Press Enter to add each constant"
                required
                error={!Array.isArray(block.allowed_list_of_constants) || block.allowed_list_of_constants.length === 0}
              />
            )}
            renderTags={() => null}
            disableCloseOnSelect
          />
          {Array.isArray(block.allowed_list_of_constants) && block.allowed_list_of_constants.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Selected Constants:
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {block.allowed_list_of_constants.map((constant, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={constant}
                    size="small"
                    onDelete={() => {
                      const newValue = block.allowed_list_of_constants?.filter((_, i) => i !== index) || [];
                      onChange({
                        ...block,
                        allowed_list_of_constants: newValue,
                      });
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      <FormControl fullWidth>
        <InputLabel>Denied Type (Optional)</InputLabel>
        <Select
          value={block.denied_type || ''}
          onChange={(e) => {
            const newValue = e.target.value as AllowedType | '';
            if (newValue === '') {
              onChange({
                ...block,
                denied_type: undefined,
                denied_regex: undefined,
                denied_list_of_constants: undefined,
              });
            } else {
              onChange({
                ...block,
                denied_type: newValue as AllowedType,
                ...(newValue === 'regex' && { denied_list_of_constants: undefined }),
                ...(newValue === 'list_of_constants' && { denied_regex: undefined }),
              });
            }
          }}
          label="Denied Type (Optional)"
        >
          <MenuItem value="">Not Selected</MenuItem>
          <MenuItem value="regex">Regex</MenuItem>
          <MenuItem value="list_of_constants">List of Constants</MenuItem>
        </Select>
      </FormControl>

      {block.denied_type === 'regex' && (
        <TextField
          label="Denied Regex (Optional)"
          value={block.denied_regex || ''}
          onChange={(e) => onChange({ ...block, denied_regex: e.target.value })}
          fullWidth
          helperText="Regular expression pattern for denied values"
        />
      )}

      {block.denied_type === 'list_of_constants' && (
        <>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={Array.isArray(block.denied_list_of_constants) ? block.denied_list_of_constants : []}
            onChange={(event, newValue) => {
              onChange({
                ...block,
                denied_list_of_constants: newValue,
              });
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Denied Constants (Optional)"
                placeholder="Enter constants"
                helperText="Press Enter to add each constant"
              />
            )}
            renderTags={() => null}
            disableCloseOnSelect
          />
          {Array.isArray(block.denied_list_of_constants) && block.denied_list_of_constants.length > 0 && (
            <Box mt={1}>
              <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                Selected Constants:
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {block.denied_list_of_constants.map((constant, index) => (
                  <Chip
                    key={index}
                    variant="outlined"
                    label={constant}
                    size="small"
                    onDelete={() => {
                      const newValue = block.denied_list_of_constants?.filter((_, i) => i !== index) || [];
                      onChange({
                        ...block,
                        denied_list_of_constants: newValue,
                      });
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

// Short Names Block Form
interface ShortNamesBlockFormProps {
  block: NamingConventionShortNamesBlock;
  defaultShortNames: Record<string, Record<string, string[]>>;
  namingConventionFormat: NamingConventionFormat;
  onChange: (block: NamingConventionShortNamesBlock) => void;
  onChangesDetected?: (changes: Array<{ service: string; resourceType: string; from: string; to: string }>) => void;
}

// Helper functions to format and parse short names based on naming convention
const formatShortNames = (names: string[], format: NamingConventionFormat): string => {
  if (names.length === 0) return '';
  
  switch (format) {
    case 'pascal_case':
      return names.map(n => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()).join('');
    case 'camel_case':
      return names[0].toLowerCase() + names.slice(1).map(n => n.charAt(0).toUpperCase() + n.slice(1).toLowerCase()).join('');
    case 'snake_case':
      return names.map(n => n.toLowerCase()).join('_');
    case 'kebab_case':
      return names.map(n => n.toLowerCase()).join('-');
    case 'screaming_snake_case':
      return names.map(n => n.toUpperCase()).join('_');
    default:
      return names.join('-');
  }
};

const parseShortNames = (formattedString: string, format: NamingConventionFormat): string[] => {
  if (!formattedString) return [];
  
  switch (format) {
    case 'pascal_case':
      // Split by capital letters: "ApiCert" -> ["Api", "Cert"] -> ["api", "cert"]
      return formattedString.split(/(?=[A-Z])/).map(s => s.toLowerCase());
    case 'camel_case':
      // Split by capital letters: "apiCert" -> ["api", "Cert"] -> ["api", "cert"]
      return formattedString.split(/(?=[A-Z])/).map(s => s.toLowerCase());
    case 'snake_case':
      // Allow "_" as delimiter (including trailing delimiters during typing)
      return formattedString.split('_').map(s => s.trim().toLowerCase());
    case 'kebab_case':
      // Allow "-" as delimiter (including trailing delimiters during typing)
      return formattedString.split('-').map(s => s.trim().toLowerCase());
    case 'screaming_snake_case':
      return formattedString.split('_').map(s => s.trim().toLowerCase());
    default:
      return formattedString.split(/[-_]/).map(s => s.trim().toLowerCase());
  }
};

const ShortNamesBlockForm: React.FC<ShortNamesBlockFormProps> = ({ block, defaultShortNames, namingConventionFormat, onChange, onChangesDetected }) => {
  // Use stored short_names if they exist (for editing), otherwise use defaults (for creation)
  const hasStoredValues = block.short_names && Object.keys(block.short_names).length > 0;
  const getOriginalShortNames = () => {
    return hasStoredValues
      ? JSON.parse(JSON.stringify(block.short_names))
      : JSON.parse(JSON.stringify(defaultShortNames));
  };
  
  const [shortNames, setShortNames] = useState<Record<string, Record<string, string[]>>>(
    getOriginalShortNames()
  );
  const [originalShortNames] = useState<Record<string, Record<string, string[]>>>(
    getOriginalShortNames()
  );
  const [expandedAll, setExpandedAll] = useState(false);
  const [expandedServices, setExpandedServices] = useState<Record<string, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    // Sync with block prop when it changes externally
    if (hasStoredValues) {
      const storedStr = JSON.stringify(block.short_names);
      const currentStr = JSON.stringify(shortNames);
      if (storedStr !== currentStr) {
        setShortNames(JSON.parse(JSON.stringify(block.short_names)));
      }
    } else {
      const defaultsStr = JSON.stringify(defaultShortNames);
      const currentStr = JSON.stringify(shortNames);
      if (defaultsStr !== currentStr && Object.keys(defaultShortNames).length > 0) {
        setShortNames(JSON.parse(JSON.stringify(defaultShortNames)));
      }
    }
    // shortNames intentionally omitted - adding it would cause re-sync on every user edit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.short_names, defaultShortNames, hasStoredValues]);

  // Calculate changes - compare current shortNames with originalShortNames
  const changes = React.useMemo(() => {
    const changeList: Array<{ service: string; resourceType: string; from: string; to: string }> = [];
    
    // Check all services and resource types
    const allServices = new Set([...Object.keys(originalShortNames), ...Object.keys(shortNames)]);
    
    allServices.forEach((service) => {
      const originalResourceTypes = originalShortNames[service] || {};
      const currentResourceTypes = shortNames[service] || {};
      const allResourceTypes = new Set([...Object.keys(originalResourceTypes), ...Object.keys(currentResourceTypes)]);
      
      allResourceTypes.forEach((resourceType) => {
        const originalValue = originalResourceTypes[resourceType] || [];
        const currentValue = currentResourceTypes[resourceType] || [];
        
        const originalFormatted = formatShortNames(originalValue, namingConventionFormat);
        const currentFormatted = formatShortNames(currentValue, namingConventionFormat);
        
        // Only add to change list if values are different
        if (originalFormatted !== currentFormatted) {
          changeList.push({
            service,
            resourceType,
            from: originalFormatted || '(empty)',
            to: currentFormatted || '(empty)',
          });
        }
      });
    });
    
    return changeList;
  }, [shortNames, originalShortNames, namingConventionFormat]);

  useEffect(() => {
    // Update parent when shortNames changes
    onChange({ ...block, short_names: shortNames });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortNames]);

  // Notify parent about changes separately
  useEffect(() => {
    if (onChangesDetected) {
      onChangesDetected(changes);
    }
  }, [changes, onChangesDetected]);

  const handleShortNameChange = (service: string, resourceType: string, formattedValue: string) => {
    // Parse the formatted string back to array
    const parsedArray = parseShortNames(formattedValue, namingConventionFormat);
    
    setShortNames((prev) => {
      const newShortNames = { ...prev };
      if (!newShortNames[service]) {
        newShortNames[service] = {};
      }
      newShortNames[service][resourceType] = parsedArray;
      return newShortNames;
    });
  };

  const toggleService = (service: string) => {
    setExpandedServices((prev) => ({
      ...prev,
      [service]: !prev[service],
    }));
  };

  // Filter services and resource types based on search query
  const filteredShortNames = React.useMemo(() => {
    if (!searchQuery.trim()) return shortNames;
    
    const query = searchQuery.toLowerCase();
    const filtered: Record<string, Record<string, string[]>> = {};
    
    Object.entries(shortNames).forEach(([service, resourceTypes]) => {
      if (service.toLowerCase().includes(query)) {
        filtered[service] = resourceTypes;
      } else {
        const filteredResourceTypes: Record<string, string[]> = {};
        Object.entries(resourceTypes).forEach(([resourceType, names]) => {
          if (resourceType.toLowerCase().includes(query)) {
            filteredResourceTypes[resourceType] = names;
          }
        });
        if (Object.keys(filteredResourceTypes).length > 0) {
          filtered[service] = filteredResourceTypes;
        }
      }
    });
    
    return filtered;
  }, [shortNames, searchQuery]);

  const hasData = Object.keys(shortNames).length > 0;
  const services = Object.keys(filteredShortNames);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        Configure short names for each service and resource type. Short names are displayed in {namingConventionFormat.replace('_', ' ')} format.
      </Typography>
      
      {/* Search and Expand/Collapse All */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search services or resource types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1 }}
        />
        <Button
          size="small"
          onClick={() => {
            setExpandedAll(!expandedAll);
            const newExpanded: Record<string, boolean> = {};
            services.forEach((service) => {
              newExpanded[service] = !expandedAll;
            });
            setExpandedServices(newExpanded);
          }}
          startIcon={expandedAll ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {expandedAll ? 'Collapse All' : 'Expand All'}
        </Button>
      </Box>

      {!hasData ? (
        <Alert severity="info">
          Loading default short names... If this message persists, please refresh the page.
        </Alert>
      ) : services.length === 0 ? (
        <Alert severity="info">
          No results found for "{searchQuery}".
        </Alert>
      ) : (
        services.map((service) => {
          const isExpanded = expandedServices[service] ?? expandedAll;
          const resourceTypes = Object.entries(filteredShortNames[service] || {});
          
          return (
            <Paper key={service} sx={{ mb: 2, p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  mb: isExpanded ? 2 : 0,
                }}
                onClick={() => toggleService(service)}
              >
                <IconButton size="small" sx={{ mr: 1 }}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                  {service.toUpperCase()}
                </Typography>
                <Chip label={`${resourceTypes.length} resource type${resourceTypes.length !== 1 ? 's' : ''}`} size="small" />
              </Box>
              
              <Collapse in={isExpanded}>
                <Box sx={{ pl: 4 }}>
                  {resourceTypes.map(([resourceType, names]) => {
                    const formattedValue = formatShortNames(names, namingConventionFormat);
                    return (
                      <Box key={resourceType} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ minWidth: 200, fontWeight: 500 }}>
                          {resourceType}:
                        </Typography>
                        <TextField
                          size="small"
                          value={formattedValue}
                          onChange={(e) => handleShortNameChange(service, resourceType, e.target.value)}
                          placeholder={`Enter short name in ${namingConventionFormat.replace('_', ' ')} format`}
                          fullWidth
                        />
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </Paper>
          );
        })
      )}
    </Box>
  );
};

